<template>
  <el-container class="app-container">
    <el-aside v-show="isShowLeftSide" class="app-aside" :style="{ width: asideCollapse ? '60px' : asideWidth + 'px' }">
      <el-scrollbar max-height="calc(100% - 40px)">
        <!-- <div style="color: white">currentSubmodule: {{ currentSubmodule?.name }}</div> -->

        <transition v-show="!isShowHomeMenu" name="fade" mode="out-in">
          <app-menu :collapse="asideCollapse" />
        </transition>

        <transition v-show="isShowHomeMenu" name="fade" mode="out-in">
          <app-home-menu :collapse="asideCollapse" />
        </transition>
      </el-scrollbar>
      <div class="app-aside__footer flex-center" :class="{ collapse: asideCollapse }">
        <el-icon @click="handleAsideCollapse">
          <back v-if="asideCollapse" color="white" />
          <back v-else color="white" />
        </el-icon>
      </div>
    </el-aside>

    <div v-if="!asideCollapse && isShowLeftSide" class="dragbar" @mousedown="handleMouseDown">
      <div class="icon">||</div>
    </div>

    <div class="app-body fs">
      <div v-if="isShowNav" class="app-nav-con">
        <app-nav />
      </div>
      <div class="app-main-con no-padding">
        <app-content :class="{ hidden: isMicroRoute }" />
        <app-micro-content :class="{ hidden: !isMicroRoute }" />
      </div>
    </div>
    <el-aside v-if="isPortal" v-show="isShowRightSide" class="app-aside right collapse">
      <AppSide />
    </el-aside>

    <AppSideTrigger v-if="isPortal" @trigger="handleAppSideTrigger" />
  </el-container>
</template>

<script setup lang="ts">
import { computed, ref, useCurrentAppInstance, DEFAULT_PAGE_NAME, onMounted, checkIsHomePage } from '@zto/zpage'
import { Back } from '@element-plus/icons'

import { UI_GLOBAL_EVENTS } from '../../consts'

import AppMenu from './app-menu/app-menu.vue'
import AppHomeMenu from './app-home-menu.vue'
import AppNav from './app-nav.vue'
import AppContent from './app-content.vue'
import AppMicroContent from './app-micro-content.vue'
import AppSideTrigger from './app-side-trigger.vue'
import AppSide from './app-side.vue'

const app = useCurrentAppInstance()

const route = app.useRoute()

const { appStore } = app.stores

const menuConfig = app.useAppConfig('menu', {})
const frameConfig = app.useAppConfig('frame')

const asideCollapse = ref<boolean>(false)

const isShowRightSide = ref<boolean>(true)
const innerIsShowHomeMenu = ref<boolean>(false) // 是否显示Home Menu，优先级最高，一般由用户手动触发

const isPortal = app.isPortal

/** 当前页面为微前端 */
const isMicroRoute = computed(() => {
  return route.path.startsWith('/m/') || !!route.meta?.micro
})

/** 是否显示框架 */
const isShowFrame = computed(() => {
  return frameConfig !== false
})

/** 是否显示菜单 */
const isShowNav = computed(() => {
  return isShowFrame.value && !!menuConfig.showNav
})

const currentSubmodule = computed(() => {
  return appStore.submodule
})

// 是否显示根目录菜单（一般为收藏夹）
const isShowHomeMenu = computed(() => {
  const _isDefaultSubmodule = appStore.isDefaultSubmodule

  if (innerIsShowHomeMenu.value) return true

  // 只有新门户展示Home Menu
  if (!isPortal) return false
  if (!menuConfig.homeMenu) return false

  if (!route.name) return true
  if (route.query?.__fav) {
    return true
  }

  return _isDefaultSubmodule
})

const isShowLeftSide = computed(() => {
  // 非portal状态下，且处于主页
  // return !isPortal && !isShowHomePage.value
  return true
})

app.emitter.on(UI_GLOBAL_EVENTS.TOGGLE_HOME_MENU, () => {
  innerIsShowHomeMenu.value = !innerIsShowHomeMenu.value
})

function handleAsideCollapse() {
  asideCollapse.value = !asideCollapse.value
}

function handleAppSideTrigger(open: boolean) {
  isShowRightSide.value = open
}

const asideWidth = ref(260)

onMounted(() => {
  let width: any = localStorage.getItem('PORTAL-MENU-WIDTH') || 260
  if (width < 138) {
    width = 138
  }
  if (width > 560) {
    width = 560
  }
  asideWidth.value = width
})

// 拖拽扩展
const handleMouseDown = e => {
  if (e.stopPropagation) e.stopPropagation()
  if (e.preventDefault) e.preventDefault()

  const ball = e.target

  document.onmousemove = event => {
    const pageX = event.pageX
    const width: any = pageX - (ball.offsetWidth || ball.clientWidth) / 2
    if (width <= 138 || width >= 560) return
    asideWidth.value = width
    localStorage.setItem('PORTAL-MENU-WIDTH', width)
  }

  function onMouseUp() {
    document.onmousemove = null
    document.removeEventListener('mouseup', onMouseUp)
    ball.onmouseup = null
  }

  document.onmouseup = onMouseUp
}
</script>

<style lang="scss" scoped>
.app-aside {
  position: relative;
  width: var(--app-sidebar-width);
  overflow-x: hidden;
  background: rgb(14, 21, 36);

  .app-aside__footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 40px;
    background: rgba(54, 147, 255, 0.1);

    :deep(i) {
      font-size: 18px;
      cursor: pointer;
    }

    :deep(.el-scrollbar__view) {
      height: 100%;
    }
  }

  .collapse.app-aside__footer {
    :deep(i) {
      transform: rotate(180deg);
    }
  }

  &.right {
    background: hsla(0, 0%, 100%, 0.51);
    width: 80px;
  }
}

.dragbar {
  cursor: col-resize;
  display: flex;
  height: 100%;
  width: 12px;
  z-index: 1;
  position: relative;
  .icon {
    border-radius: 0 4px 4px 0;
    box-shadow: 6px 0 6px 0 rgba(33, 79, 135, 0.02);
    width: 12px;
    height: 40px;
    line-height: 40px;
    position: absolute;
    top: calc(50% - 20px);
    background-color: #1a243a;
    color: #ccc;
    display: flex;
    align-items: center;
  }
}

.app-body {
  overflow: auto;
  background: linear-gradient(180deg, #d6e8fb 0%, #f2f6f9 100%);
  .app-nav-con {
    height: var(--app-nav-height);
  }

  .app-main-con {
    height: calc(100% - var(--app-nav-height) - 20px);
  }
}
</style>
